// EmailJS service — Safe Harbor Veterans
// Templates configured by user in EmailJS dashboard.
// IMPORTANT: Replace the empty template_xxx IDs below with your actual EmailJS template IDs.
// When a template ID is empty, the frontend will skip that email gracefully.
import emailjs from "@emailjs/browser";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Map our notification template names to EmailJS template IDs.
// User has these templates ready: Welcome New User, New Seeker Request, New Listing Submitted,
// Listing Approval, Listing Denied, Listing Expiring Soon, Listing Removed, Confirmation.
// Replace the right-hand IDs once you grab them from your EmailJS dashboard.
export const TEMPLATE_IDS = {
  welcome_new_user: process.env.REACT_APP_EMAILJS_TPL_WELCOME || "",
  new_seeker_request: process.env.REACT_APP_EMAILJS_TPL_NEW_SEEKER || "",
  new_listing_submitted: process.env.REACT_APP_EMAILJS_TPL_NEW_LISTING || "",
  listing_approval: process.env.REACT_APP_EMAILJS_TPL_LISTING_APPROVAL || "",
  listing_denied: process.env.REACT_APP_EMAILJS_TPL_LISTING_DENIED || "",
  listing_expiring_soon: process.env.REACT_APP_EMAILJS_TPL_LISTING_EXPIRING || "",
  listing_removed: process.env.REACT_APP_EMAILJS_TPL_LISTING_REMOVED || "",
  confirmation: process.env.REACT_APP_EMAILJS_TPL_CONFIRMATION || "",
  // Additional placeholders for future templates
  new_referral: process.env.REACT_APP_EMAILJS_TPL_NEW_REFERRAL || "",
  vacancy_alert: process.env.REACT_APP_EMAILJS_TPL_VACANCY_ALERT || "",
};

let initialized = false;
const init = () => {
  if (initialized || !PUBLIC_KEY) return;
  try {
    emailjs.init({ publicKey: PUBLIC_KEY });
    initialized = true;
  } catch (e) {
    console.warn("[EmailJS] init() failed:", e?.message || e);
  }
};

export const sendEmail = async (templateName, params) => {
  init();
  const templateId = TEMPLATE_IDS[templateName];
  if (!SERVICE_ID || !PUBLIC_KEY) {
    console.warn("[EmailJS] Missing service/public key — email skipped:", templateName);
    return { ok: false, reason: "missing_credentials" };
  }
  if (!templateId) {
    console.info(`[EmailJS] Template '${templateName}' not configured (empty ID) — email skipped.`);
    return { ok: false, reason: "missing_template_id" };
  }
  try {
    const resp = await emailjs.send(SERVICE_ID, templateId, params || {}, { publicKey: PUBLIC_KEY });
    return { ok: true, resp };
  } catch (e) {
    console.error("[EmailJS] Send failed:", templateName, e);
    return { ok: false, reason: e?.text || String(e) };
  }
};

// Pulls pending email notifications from the backend and dispatches them via EmailJS.
// Called from a periodic poller in Layout when user is signed in.
export const flushPendingEmails = async (api, user) => {
  if (!user) return;
  try {
    const { data } = await api.get("/notifications/pending-email");
    for (const n of data) {
      const params = {
        to_email: user.email,
        to_name: user.full_name,
        title: n.title,
        message: n.message,
        notification_type: n.notification_type,
      };
      const res = await sendEmail(n.email_template, params);
      if (res.ok) {
        await api.post(`/notifications/${n.id}/email-sent`).catch(() => {});
      } else if (res.reason === 'missing_template_id' || res.reason === 'missing_credentials') {
        // Template not configured — skip this notification, don't block the rest
        console.info('[EmailJS] skipping unconfigured template for', n.id, res.reason);
      } else {
        console.warn('[EmailJS] send failed for notification', n.id, res.reason);
        break;
      }
    }
  } catch (e) {
    console.warn("[EmailJS] flushPendingEmails failed:", e?.response?.status || e?.message || e);
  }
};
